import { useSelector } from 'react-redux'

import { skipToken } from '@reduxjs/toolkit/query'

import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { isNeuropsychEvaluationProgram } from '@lyrahealth-inc/shared-app-logic/src/features/programConfigs/programHelpers'
import { Episode } from '@lyrahealth-inc/shared-app-logic/src/models/episodes/Episodes'
import { NoteStatusStates } from '@lyrahealth-inc/shared-app-logic/src/models/notes/Notes'

import { useDevMocks } from '../../../common/components/devTools/DevMocksContext'
import { NP_TESTER_ROLES } from '../../../common/constants/appConstants'
import { hasRole } from '../../../common/utils/utils'
import { getAuthRoles } from '../../../data/auth/authSelectors'
import { useGetNotesQuery } from '../../clientNotes/data/notesApi'
import { isIntakeNote } from '../../clientNotes/utils/npSessionNote'

export interface NpTestPlanEntrypoint {
  isVisible: boolean
  /** The tab still opens; only the build is held back until the intake note is completed. */
  isBlocked: boolean
  /** Separates "the intake session has not happened yet" from "the note is still open". */
  hasIntakeNote: boolean
  isLoading: boolean
}

/**
 * [ENGBEND-12942] Scoped to the episode selected in the chart rather than to the member, since a
 * member can be enrolled in several services at once alongside a neuropsych evaluation.
 *
 * The dev mocks need no production guard here: `DevMocksProvider` never reads them in production.
 */
export const useNpTestPlanEntrypoint = (episode?: Episode): NpTestPlanEntrypoint => {
  const flags = useFlags()
  const roles = useSelector(getAuthRoles)
  const { forceEpisode, forceRole, intakeNoteCompleted } = useDevMocks().devMocks.npTestPlan

  const isNeuropsychProvider = forceRole || hasRole(roles, NP_TESTER_ROLES)
  const isNeuropsychEpisode = forceEpisode || isNeuropsychEvaluationProgram(episode?.program_name)
  const isVisible = Boolean(flags.npTestingPlans && isNeuropsychProvider && isNeuropsychEpisode)

  // Intake notes are real data, so the gate reads the notes service rather than the mocked
  // test plan endpoints — unless the dev mock pins the answer.
  const isIntakeNoteMocked = intakeNoteCompleted !== undefined
  const shouldReadNotes = isVisible && !isIntakeNoteMocked
  const { data: notes = [], isLoading } = useGetNotesQuery(
    shouldReadNotes && episode?.id ? { episodeId: episode.id } : skipToken,
  )

  const intakeNotes = notes.filter(isIntakeNote)
  const hasIntakeNote = isIntakeNoteMocked || intakeNotes.length > 0
  const isIntakeNoteCompleted =
    intakeNoteCompleted ?? intakeNotes.some((note) => note.status === NoteStatusStates.COMPLETED)

  return {
    isVisible,
    isBlocked: isVisible && !isIntakeNoteCompleted,
    hasIntakeNote,
    isLoading: shouldReadNotes && isLoading,
  }
}
